import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy } from '@angular/core';
import { AI_TOOLS } from 'src/app/data/ai/ai-tools.data';
import { URLS } from 'src/app/data/navigation/navigation.data';
import { COLOR_TOOL_KEYS, TOOLS } from 'src/app/data/product/product.data';
import { Product } from 'src/app/model/product/product.model';
import { toolLine } from 'src/app/util/tool-line';

// The /tools landing page: the in-browser Web Tools in three groups, the color tool, the generators, then the
// converters (owner 2026-10-11); laid out as the blog (owner 2026-10-12): the Apps & Plugins hero band with a chip
// per group, then a row per group, its short card at the left and its tools' card at the right. Browser
// extensions live on their own /plugins hub.
@Component( {
  selector: 'app-tools-page',
  templateUrl: './tools-page.component.html',
  styleUrls: [ './tools-page.component.scss', '../../plugin-pages/plugins-page/plugins-page.component.scss' ],
} )
export class ToolsPageComponent implements AfterViewInit, OnDestroy {

  URLS = URLS;

  private readonly all: Product[] = TOOLS[ 0 ]?.products ?? [];

  readonly sections: { key: string; eyebrow: string; title: string; desc: string; icon?: string; tools: Product[] }[] = [
    {
      // first (owner 2026-10-19): the AI tools, no /ai area of their own
      key: 'ai',
      eyebrow: 'Plan',
      title: 'AI Tools',
      desc: 'Find out what an app that uses AI would cost a month on every model, with prices updated every day',
      icon: './assets/product-lines/tools/ai-cost-card.svg',
      tools: AI_TOOLS,
    },
    {
      key: 'color',
      eyebrow: 'Explore',
      title: 'Color Tools',
      desc: 'Explore any color, check the contrast of any text and see any image as color blind people do',
      icon: './assets/product-lines/tools/color-tools.svg',
      tools: this.all.filter( t => COLOR_TOOL_KEYS.includes( t.key ) ),
    },
    {
      key: 'generators',
      eyebrow: 'Create',
      title: 'Generators',
      desc: 'Build palettes and gradients and turn them into wallpapers, free in your browser',
      icon: './assets/product-lines/tools/web-tool.svg',
      tools: this.all.filter( t => !t.key.includes( '-to-' ) && !COLOR_TOOL_KEYS.includes( t.key ) ),
    },
    {
      key: 'converters',
      eyebrow: 'Convert',
      title: 'Converters',
      desc: 'Convert any color between HEX and every other format, both ways, with a live preview',
      icon: './assets/product-lines/tools/converters.svg',
      tools: this.all.filter( t => t.key.includes( '-to-' ) ),
    },
  ];

  private resizeObserver?: ResizeObserver;

  constructor( private host: ElementRef<HTMLElement>, private zone: NgZone ) {
  }

  /** each group's own card is at least one row of its tools' card tall (owner 2026-10-12): measured, as a card's
   *  height follows its width */
  ngAfterViewInit(): void {
    if ( typeof ResizeObserver === 'undefined' ) { return; }
    this.zone.runOutsideAngular( () => {
      this.resizeObserver = new ResizeObserver( () => this.matchHeads() );
      this.host.nativeElement.querySelectorAll( '.tl-grid' ).forEach( grid => this.resizeObserver!.observe( grid ) );
    } );
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private matchHeads(): void {
    this.host.nativeElement.querySelectorAll<HTMLElement>( '.tl-row' ).forEach( row => {
      const head = row.querySelector<HTMLElement>( '.tl-head' );
      const grid = row.querySelector<HTMLElement>( '.tl-grid' );
      const card = grid?.querySelector<HTMLElement>( '.tl-card' );
      if ( !head || !grid || !card ) { return; }
      const style = getComputedStyle( grid );
      const frame = parseFloat( style.paddingTop ) + parseFloat( style.paddingBottom )
        + parseFloat( style.borderTopWidth ) + parseFloat( style.borderBottomWidth );
      head.style.minHeight = `${ Math.round( card.offsetHeight + frame ) }px`;
    } );
  }

  /** a converter's line, each format with what its numbers are (util/tool-line.ts) */
  line( tool: Product ): string {
    return toolLine( tool );
  }

  /** a group chip: scroll to its row (the app scrolls on <body>, where a smooth scrollIntoView does nothing) */
  jumpTo( key: string, event?: Event ): void {
    event?.preventDefault();
    const row = document.getElementById( 'tools-' + key );
    if ( !row ) { return; }
    row.focus( { preventScroll: true } );
    const scroller = document.body.scrollHeight > document.body.clientHeight + 4 ? document.body : document.documentElement;
    const offset = window.matchMedia( '(max-width: 1023px)' ).matches ? 70 : 90;
    const from = scroller.scrollTop;
    const to = Math.max( 0, row.getBoundingClientRect().top + from - offset );
    const start = performance.now();
    const step = ( now: number ) => {
      const t = Math.min( 1, ( now - start ) / 420 );
      scroller.scrollTop = from + ( to - from ) * ( 1 - Math.pow( 1 - t, 3 ) );
      if ( t < 1 ) { requestAnimationFrame( step ); }
    };
    requestAnimationFrame( step );
  }
}
